import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { freshnessTier, fmtAccuracy } from "@/lib/geo";

function memberIcon(m, isMe) {
  const tier = freshnessTier(m.last_updated);
  const stale = tier.key === "stale" || tier.key === "gone";
  const color = m.color || "#3B82F6";
  const emoji = m.emoji ? `<span class="ff-emoji">${escapeHtml(m.emoji)}</span>` : "";
  const acc = fmtAccuracy(m.accuracy);
  const accText = acc ? ` · ${acc}` : "";
  return L.divIcon({
    className: "",
    html: `
      <div class="ff-marker ${isMe ? "ff-me" : ""} ${stale ? "ff-stale" : ""}">
        <div class="ff-dot" style="background:${color}">${emoji}</div>
        <div class="ff-chip">${escapeHtml(m.name)}${isMe ? " (you)" : ""}
          <span class="ff-time" style="color:${tier.color}">${tier.label}${accText}</span>
        </div>
      </div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

function meetIcon(label) {
  return L.divIcon({
    className: "",
    html: `<div class="ff-meet"><div class="ff-meet-core">★</div><div class="ff-meet-label">${escapeHtml(label || "Meet Here")}</div></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function Recenter({ center, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom() < 15 ? 17 : map.getZoom(), { animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);
  return null;
}

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      if (onMapClick) onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

export default function MapView({ myPos, members, meet, myUserId, recenterTrigger, onMapClick, onFriendClick }) {
  const center = myPos ? [myPos.lat, myPos.lng] : [51.5, -0.09];

  const markers = useMemo(() => {
    return members.map((m) => {
      const isMe = m.user_id === myUserId;
      return (
        <Marker
          key={m.user_id}
          position={[m.lat, m.lng]}
          icon={memberIcon(m, isMe)}
          zIndexOffset={isMe ? 1000 : 0}
          eventHandlers={
            !isMe && onFriendClick ? { click: () => onFriendClick(m) } : undefined
          }
        />
      );
    });
  }, [members, myUserId, onFriendClick]);

  return (
    <MapContainer
      center={center}
      zoom={17}
      zoomControl={true}
      className="h-full w-full"
      attributionControl={true}
    >
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        maxNativeZoom={19}
        maxZoom={22}
        attribution="Imagery &copy; Esri"
      />
      <TileLayer
        url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
        maxNativeZoom={19}
        maxZoom={22}
      />
      <Recenter center={myPos ? [myPos.lat, myPos.lng] : null} trigger={recenterTrigger} />
      <ClickHandler onMapClick={onMapClick} />
      {markers}
      {meet && meet.lat != null && (
        <Marker position={[meet.lat, meet.lng]} icon={meetIcon(meet.label)} zIndexOffset={2000} />
      )}
    </MapContainer>
  );
}
